import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Location } from "app/models/projectDevice.model";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

interface NominatimSearchResult {
    lat: string;
    lon: string;
    display_name: string;
}

interface NominatimReverseResult {
    display_name?: string;
}

@Injectable({ providedIn: "root" })
export class GeocodingService {
    private readonly searchUrl = "https://nominatim.openstreetmap.org/search";
    private readonly reverseUrl = "https://nominatim.openstreetmap.org/reverse";

    constructor(private readonly http: HttpClient) {}

    // Geocode an address into coordinates.
    geocodeAddress(address: string): Observable<Location | null> {
        // Build the search params.
        const params = new HttpParams()
            .set("q", address.trim())
            .set("format", "jsonv2")
            .set("limit", "1")
            .set("addressdetails", "0")
            .set("accept-language", this.getPreferredLanguage());

        // Map the first result into the app location shape.
        return this.http.get<NominatimSearchResult[]>(this.searchUrl, { params }).pipe(
            map((results) => {
                if (!results?.length) {
                    return null;
                }

                return this.mapSearchResult(results[0]);
            }),
        );
    }

    // Reverse geocode coordinates into a display address.
    reverseGeocode(latitude: number, longitude: number): Observable<string | null> {
        // Build the reverse lookup params.
        const params = new HttpParams()
            .set("lat", latitude.toString())
            .set("lon", longitude.toString())
            .set("format", "jsonv2")
            .set("zoom", "18")
            .set("addressdetails", "0")
            .set("accept-language", this.getPreferredLanguage());

        // Extract the display name when available.
        return this.http.get<NominatimReverseResult>(this.reverseUrl, { params }).pipe(map((result) => result?.display_name ?? null));
    }

    // Map the provider result into a device location.
    private mapSearchResult(result: NominatimSearchResult): Location | null {
        // Parse the numeric coordinates.
        const latitude = Number(result.lat);
        const longitude = Number(result.lon);

        // Guard against invalid provider data.
        if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
            return null;
        }

        return {
            address: result.display_name,
            latitude,
            longitude,
        };
    }

    // Get the browser language for geocoding results.
    private getPreferredLanguage(): string {
        // Reuse the browser language when available.
        if (typeof navigator !== "undefined" && navigator.language) {
            return navigator.language;
        }

        return "en";
    }
}
